import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

import axios from 'axios';
// @ts-ignore
import { cloneDeep, isEqual } from 'lodash';

import { BOBBY_API_ROOT } from '@/composables/api/constants';
import { formatQueryParams } from '@/composables/api/helpers';
import type { Depot } from '@/composables/api/userDepots/types';
import constants from '@/config/constants';
import dayjs from '@/plugins/dayjs';
import apiClient from '@/services/api';
import type {
  RolesAndScopesResponse,
  ScopeNode,
  ScopeTreePayload,
  ScopeTreeResponse,
  SelectedScope,
  UpdateSelectedDepotPayload
} from './types/scopes';

const removeTempSuco = (scopeTree: ScopeNode[]): ScopeNode[] =>
  scopeTree.map((scope) => ({
    ...scope,
    children: scope.children.length ? removeTempSuco(scope.children) : []
  }));

const sortScopeTree = (scopeTree: ScopeNode[]): ScopeNode[] => {
  return scopeTree
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((scope) => ({
      ...scope,
      children: scope.children.length ? sortScopeTree(scope.children) : []
    }));
};

const useScopesStore = defineStore('scopes', () => {
  // State
  const availableRolesAndScopes = ref<RolesAndScopesResponse | null>(null);
  const scopeTree = ref<ScopeNode[]>([]);
  const lastLoadedScopeTree = ref<ScopeTreePayload | null>(null);
  const selectedScope = ref<SelectedScope>({ type: null, data: null });

  // Getters
  const selectedDepot = computed((): Depot | null => selectedScope.value.data);

  // Actions
  const loadAvailableRolesAndScopes = async (): Promise<void> => {
    try {
      const { data } = await apiClient.get<RolesAndScopesResponse>(
        `${BOBBY_API_ROOT}/rolesAndScopes`
      );
      availableRolesAndScopes.value = data;
    } catch (error) {
      // Silently ignore CanceledError that occurs during logout
      if (!axios.isCancel(error)) {
        throw error;
      }
    }
  };

  const loadScopeTree = async (
    payload: ScopeTreePayload,
    // Refetch even when the payload matches the last loaded one
    force = false
  ): Promise<void> => {
    try {
      if (!payload.level && payload.level !== 0) payload.level = 5;
      if (!force && isEqual(lastLoadedScopeTree.value, payload)) return;

      lastLoadedScopeTree.value = payload;

      const { level, path } = payload;
      const params = formatQueryParams({ level, path });
      const { data } = await apiClient.get<ScopeTreeResponse>(
        `${BOBBY_API_ROOT}/scopes`,
        {
          params
        }
      );

      const tree: ScopeNode[] | ScopeTreeResponse[] =
        data.scope === 'ADMIN' ? data.children : [data];
      const scopes = removeTempSuco(tree);
      scopeTree.value = sortScopeTree(scopes);

      lastLoadedScopeTree.value = payload;
    } catch (error) {
      // Clear cache on any failure so subsequent calls will retry.
      // This includes CanceledError during logout, which should not block
      // future reload attempts when the user logs back in.
      lastLoadedScopeTree.value = null;

      // Only throw non-cancellation errors; silently ignore logout cancellations
      if (!axios.isCancel(error)) {
        throw error;
      }
    }
  };

  const updateSelectedScope = (scope: SelectedScope): void => {
    const { type } = scope;
    const { ADMIN, TENANT } = constants.scopeTypes;
    // if Admin or Tenant context
    if (type && [ADMIN, TENANT].includes(type)) {
      selectedScope.value = { data: null, type };
      return;
    }

    const scopeUpdatePayload: SelectedScope = cloneDeep(scope);

    if (scopeUpdatePayload.data) {
      const { tenant, countryCode, group, id } = scopeUpdatePayload.data;
      scopeUpdatePayload.data.path = `${tenant.id}.${countryCode}.${group.id}.${id}`;
    }
    dayjs.tz.setDefault(scopeUpdatePayload.data?.timeZone ?? undefined);
    selectedScope.value = scopeUpdatePayload;
  };

  const updateSelectedDepot = ({
    selectedDepot,
    externalDepotId,
    tenantId
  }: UpdateSelectedDepotPayload): void => {
    const { data } = selectedScope.value;
    if (
      !data ||
      data.externalDepotId !== externalDepotId ||
      data.tenant.id !== tenantId
    ) {
      // sets the permissions and the selected scope for the depot,
      // guess the permissions can change with the depot
      updateSelectedScope({
        type: selectedDepot ? constants.scopeTypes.DEPOT : null,
        data: selectedDepot ?? null
      });
    }
  };

  // Custom reset that also resets the global timezone
  // Called after $reset by the logout process
  const resetTimezone = (): void => {
    dayjs.tz.setDefault(undefined);
  };

  return {
    availableRolesAndScopes,
    scopeTree,
    selectedScope,
    selectedDepot,
    loadAvailableRolesAndScopes,
    loadScopeTree,
    updateSelectedScope,
    updateSelectedDepot,
    resetTimezone
  };
});

export default useScopesStore;
